import { SubscriptionServiceResponseModel, Subscription, CreateSubscriptionServiceModel, UpdateSubscriptionServiceModel } from '../types';
import { detectFormChanges } from './changeDetector';

/**
 * Maps API subscription response to UI subscription model
 */
export const mapApiSubscriptionToUi = (apiSubscription: SubscriptionServiceResponseModel): Subscription => {
  // Map status number to string
  const getStatusString = (status: number): 'Active' | 'Inactive' | 'Pending' | 'Expired' => {
    switch (status) {
      case 0: return 'Active';
      case 1: return 'Inactive';
      case 2: return 'Pending';
      case 3: return 'Expired';
      default: return 'Inactive';
    }
  };

  // Extract admin data from API response if available
  const subscriptionAdmin = (apiSubscription as any).subscriptionAdmin;
  const adminFirstName = subscriptionAdmin?.firstName || '';
  const adminLastName = subscriptionAdmin?.lastName || '';
  const adminEmail = subscriptionAdmin?.email || '';

  const subscription: Subscription & { adminFirstName?: string; adminLastName?: string; adminEmail?: string } = {
    id: apiSubscription.id,
    subscriptionId: apiSubscription.id,
    sponsorName: apiSubscription.sponsorName,
    tenant: (apiSubscription as any).tenantName || apiSubscription.tenantId || 'Loading...', // Use tenantName if available, fallback to tenantId
    startDate: new Date(apiSubscription.startDate),
    endDate: new Date(apiSubscription.endDate),
    salesAgent: apiSubscription.salesAgentName,
    modules: [], // Not available in API response - empty array for now
    status: getStatusString(apiSubscription.status),
  };

  // Add admin fields if available
  if (adminFirstName || adminLastName || adminEmail) {
    (subscription as any).adminFirstName = adminFirstName;
    (subscription as any).adminLastName = adminLastName;
    (subscription as any).adminEmail = adminEmail;
  }

  return subscription;
};

/**
 * Maps UI subscription form data to API create model
 */
export const mapUiToCreateApiModel = (formData: {
  sponsorName: string;
  tenant: string;
  startDate: Date;
  endDate: Date;
  salesAgent: string;
  modules: string[];
  status: 'Active' | 'Inactive' | 'Pending' | 'Expired';
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}): CreateSubscriptionServiceModel => {
  // Map status string to number
  const getStatusNumber = (status: string): number => {
    switch (status) {
      case 'Active': return 0;
      case 'Inactive': return 1;
      case 'Pending': return 2;
      case 'Expired': return 3;
      default: return 1;
    }
  };

  return {
    startDate: formData.startDate.toISOString(),
    endDate: formData.endDate.toISOString(),
    sponsorName: formData.sponsorName,
    salesAgentName: formData.salesAgent,
    subscriptionAdmin: {
      firstName: formData.adminFirstName,
      lastName: formData.adminLastName,
      email: formData.adminEmail,
    },
    tenantId: formData.tenant, // Send tenant ID from form
    status: getStatusNumber(formData.status),
  };
};

/**
 * Maps UI subscription form data to API update model
 */
export const mapUiToUpdateApiModel = (formData: {
  sponsorName: string;
  tenant: string;
  startDate: Date;
  endDate: Date;
  salesAgent: string;
  modules: string[];
  status: 'Active' | 'Inactive' | 'Pending' | 'Expired';
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}): UpdateSubscriptionServiceModel => {
  // Map status string to number
  const getStatusNumber = (status: string): number => {
    switch (status) {
      case 'Active': return 0;
      case 'Inactive': return 1;
      case 'Pending': return 2;
      case 'Expired': return 3;
      default: return 1;
    }
  };

  return {
    startDate: formData.startDate.toISOString(),
    endDate: formData.endDate.toISOString(),
    sponsorName: formData.sponsorName,
    salesAgentName: formData.salesAgent,
    subscriptionAdmin: {
      firstName: formData.adminFirstName,
      lastName: formData.adminLastName,
      email: formData.adminEmail,
    },
    tenantId: formData.tenant, // Send tenant ID from form
    status: getStatusNumber(formData.status),
  };
};

/**
 * Maps UI subscription form data to API update model with only changed fields for PATCH requests
 */
export const mapUiToUpdateApiModelPatch = (
  originalSubscription: Subscription,
  formData: {
    sponsorName: string;
    tenant: string;
    startDate: Date;
    endDate: Date;
    salesAgent: string;
    modules: string[];
    status: 'Active' | 'Inactive' | 'Pending' | 'Expired';
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
  }
): UpdateSubscriptionServiceModel => {
  // Map status string to number
  const getStatusNumber = (status: string): number => {
    switch (status) {
      case 'Active': return 0;
      case 'Inactive': return 1;
      case 'Pending': return 2;
      case 'Expired': return 3;
      default: return 1;
    }
  };

  // Get original admin data (if available from subscription)
  const originalAdminFirstName = (originalSubscription as any).adminFirstName || '';
  const originalAdminLastName = (originalSubscription as any).adminLastName || '';
  const originalAdminEmail = (originalSubscription as any).adminEmail || '';

  // Create the full update object
  const fullUpdate: UpdateSubscriptionServiceModel = {
    startDate: formData.startDate.toISOString(),
    endDate: formData.endDate.toISOString(),
    sponsorName: formData.sponsorName,
    salesAgentName: formData.salesAgent,
    subscriptionAdmin: {
      firstName: formData.adminFirstName,
      lastName: formData.adminLastName,
      email: formData.adminEmail,
    },
    tenantId: formData.tenant, // Send tenant ID from form
    status: getStatusNumber(formData.status),
  };

  // Create the original object for comparison
  const originalApiModel: UpdateSubscriptionServiceModel = {
    startDate: originalSubscription.startDate.toISOString(),
    endDate: originalSubscription.endDate.toISOString(),
    sponsorName: originalSubscription.sponsorName,
    salesAgentName: originalSubscription.salesAgent,
    subscriptionAdmin: {
      firstName: originalAdminFirstName,
      lastName: originalAdminLastName,
      email: originalAdminEmail,
    },
    tenantId: originalSubscription.tenant, // Use tenant from original subscription
    status: getStatusNumber(originalSubscription.status),
  };

  // Detect changes with deep comparison for subscriptionAdmin
  const changes = detectFormChanges(originalApiModel, fullUpdate);
  
  // Check if subscriptionAdmin has actually changed (deep comparison)
  if (changes.subscriptionAdmin) {
    const originalAdmin = originalApiModel.subscriptionAdmin;
    const updatedAdmin = fullUpdate.subscriptionAdmin;
    
    if (originalAdmin && updatedAdmin) {
      const adminChanged = 
        originalAdmin.firstName !== updatedAdmin.firstName ||
        originalAdmin.lastName !== updatedAdmin.lastName ||
        originalAdmin.email !== updatedAdmin.email;
      
      if (!adminChanged) {
        // Admin hasn't changed, remove it from changes
        delete changes.subscriptionAdmin;
      }
    }
  }

  return changes;
};
